import * as z from "zod";

const findingQualifierSchema = z.string();

const phraseSchema = z.object({
  id: z.string(),
  bucket: z.enum([
    "descriptor",
    "location",
    "action",
    "check",
    "finding",
    "disclaimer",
  ]),
  text: z.string(),
  supportsNegation: z.boolean().optional(),
  supportsQualifier: z.boolean().optional(),
  qualifierPosition: z
    .enum(["before_finding", "after_no"])
    .nullable()
    .optional(),
});

const observationBlockSchema = z.object({
  targetLabel: z.string(),
  descriptorIds: z.array(z.string()),
  locationIds: z.array(z.string()),
  actionIds: z.array(z.string()),
  checkIds: z.array(z.string()),
  findingIds: z.array(z.string()),
  disclaimerIds: z.array(z.string()),
  findingNegationByFindingId: z.record(z.string(), z.boolean().optional()),
  findingQualifierByFindingId: z.record(
    z.string(),
    findingQualifierSchema.optional(),
  ),
  notes: z.string().default(""),
});

const reportItemSchema = z.object({
  id: z.string(),
  areaId: z.string(),
  title: z.string(),
  included: z.boolean(),
  generatedParagraph: z.string().nullish(),
  blocks: z.array(observationBlockSchema),
  phrases: z.array(phraseSchema),
  recommendations: z
    .array(
      z.object({
        text: z.string(),
        severity: z.enum(["maint", "asap", "critical"]),
      }),
    )
    .default([]),
});

const reportAreaSchema = z.object({
  id: z.string(),
  name: z.string(),
  sortOrder: z.number().optional(),
});

export const generateSrasTestReportInputSchema = z.object({
  organizationId: z.string(),
  surveyId: z.string(),
  templateId: z.string().optional(),

  metadata: z
    .object({
      vesselName: z.string().nullish(),
      make: z.string().nullish(),
      model: z.string().nullish(),
      loa: z.union([z.string(), z.number()]).nullish(),
      beam: z.union([z.string(), z.number()]).nullish(),
      draft: z.union([z.string(), z.number()]).nullish(),
      year: z.union([z.string(), z.number()]).nullish(),
      hullMaterial: z.string().nullish(),
      fuel: z.string().nullish(),
      engine: z.string().nullish(),
      hin: z.string().nullish(),
      mouldNumber: z.string().nullish(),
      officialNumber: z.string().nullish(),
      registeredTonnage: z.union([z.string(), z.number()]).nullish(),
      ssrNumber: z.string().nullish(),

      location: z.string().nullish(),
      surveyDate: z.union([z.string(), z.number(), z.date()]).nullish(),
      afloatAshore: z.string().nullish(),
    })
    .partial()
    .nullish(),

  editorState: z.object({
    filename: z.string().optional(),
    variables: z
      .record(z.string(), z.union([z.string(), z.number(), z.null()]))
      .optional(),
    areas: z.array(reportAreaSchema),
    items: z.array(reportItemSchema),
  }),
});

export type GenerateSrasTestReportInput = z.infer<
  typeof generateSrasTestReportInputSchema
>;

export const generateSrasSurveyItemAiParagraphInputSchema = z.object({
  organizationId: z.string(),
  surveyId: z.string(),
  itemId: z.string(),
  payload: z.unknown(),
});

export const generateSrasSurveyItemAiParagraphResponseSchema = z.object({
  paragraph: z.string(),
});

export type GenerateSrasSurveyItemAiParagraphInput = z.infer<
  typeof generateSrasSurveyItemAiParagraphInputSchema
>;
